import React from 'react';
import { CarWindowType } from './GlassControl.types';

type OverlayVariant = 'normal' | 'replaced' | 'absence';

interface WindowOverlayProps {
  type: CarWindowType;
  variant?: OverlayVariant;
  className?: string;
  style?: React.CSSProperties;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Konfiguracja ścieżek SVG dla każdego typu okna
const windowPaths: Record<CarWindowType, {
  width: number;
  height: number;
  viewBox: string;
  path: string;
  clipPathId: string;
  foreignObjectSize?: { x: number; y: number; width: number; height: number };
}> = {
  FRONT: {
    width: 196,
    height: 128,
    viewBox: '0 0 196 128',
    path: 'M174.857 126C112.257 114.251 83.9864 114.924 19.3674 125.666C16.6749 110.223 5.70291 64.6924 3.54894 54.4851C0.85648 41.7261 -0.48975 26.2809 3.54894 22.5875C7.58763 18.8941 28.4731 -0.547133 101.151 1.09864C160.385 2.44 190.675 14.8633 194.041 26.6167C195.724 30.9816 195.656 42.9348 190.002 61.2004C184.348 79.466 177.549 112.011 174.857 126Z',
    clipPathId: 'bgblur_front',
    foreignObjectSize: { x: -3, y: -3, width: 202, height: 133.166 },
  },
  REAR: {
    width: 134,
    height: 91,
    viewBox: '0 0 134 91',
    path: 'M117.049 81.7227C105.682 89.1702 79.302 90.5039 66.7991 90.2408C54.3155 90.5039 28.4161 89.1702 17.0488 81.7227C8.65409 76.2227 4.54883 59.7227 3.54883 46.2227L0.548828 0.722656C7.21549 3.05599 29.1988 7.72266 66.7988 7.72266C104.399 7.72266 126.382 3.05599 133.049 0.722656L131.049 45.7227C130.049 59.2227 127.733 74.7227 117.049 81.7227Z',
    clipPathId: 'bgblur_rear',
    foreignObjectSize: { x: -3, y: -3, width: 140, height: 97 },
  },
  LEFT_FRONT_DOOR: {
    width: 126,
    height: 48,
    viewBox: '0 0 126 48',
    path: 'M0.5 32.9102C58.8915 2.4964 95.6323 0.292822 124.5 0.51321C124.063 1.39461 119.383 11.2236 104.161 43.4883C74.2439 47.1908 22.5882 47.6757 0.5 47.4553V32.9102Z',
    clipPathId: 'bgblur_left_front_door',
    foreignObjectSize: { x: -3, y: -3, width: 131.303, height: 54 },
  },
  LEFT_REAR_DOOR: {
    width: 102,
    height: 42,
    viewBox: '0 0 102 42',
    path: 'M7.12189 0.5L0.585938 41.5C41.3703 41.5 84.2461 37.0914 100.586 34.8871C99.0609 32.4624 93.5271 23.3806 83.5925 6.45161C62.1546 1.69032 23.6796 0.5 7.12189 0.5Z',
    clipPathId: 'bgblur_left_rear_door',
    foreignObjectSize: { x: -3, y: -3, width: 107.423, height: 48 },
  },
  LEFT_TRIANGLE: {
    width: 54,
    height: 30,
    viewBox: '0 0 54 30',
    path: 'M0.977539 0.640625L18.0915 28.6406C28.4037 27.3383 49.818 22.9104 52.9775 15.6174C45.0788 8.32435 15.0197 2.59411 0.977539 0.640625Z',
    clipPathId: 'bgblur_left_triangle',
    foreignObjectSize: { x: -3, y: -3, width: 59.5771, height: 35.1777 },
  },
  RIGHT_FRONT_DOOR: {
    width: 126,
    height: 48,
    viewBox: '0 0 126 48',
    path: 'M124.803 32.9102C66.4117 2.4964 29.6709 0.292822 0.803223 0.51321C1.24062 1.39461 5.92068 11.2236 21.1418 43.4883C51.0593 47.1908 102.715 47.6757 124.803 47.4553V32.9102Z',
    clipPathId: 'bgblur_right_front_door',
    foreignObjectSize: { x: -3, y: -3, width: 131.303, height: 54 },
  },
  RIGHT_REAR_DOOR: {
    width: 102,
    height: 42,
    viewBox: '0 0 102 42',
    path: 'M94.301 0.5L100.837 41.5C60.0526 41.5 17.1768 37.0914 0.836914 34.8871C2.36197 32.4624 7.89574 23.3806 17.8304 6.45161C39.2683 1.69032 77.7432 0.5 94.301 0.5Z',
    clipPathId: 'bgblur_right_rear_door',
    foreignObjectSize: { x: -3, y: -3, width: 107.423, height: 48 },
  },
  RIGHT_TRIANGLE: {
    width: 54,
    height: 30,
    viewBox: '0 0 54 30',
    path: 'M52.5996 0.640625L35.4857 28.6406C25.1734 27.3383 3.7591 22.9104 0.599609 15.6174C8.49834 8.32435 38.5574 2.59411 52.5996 0.640625Z',
    clipPathId: 'bgblur_right_triangle',
    foreignObjectSize: { x: -3, y: -3, width: 59.5771, height: 35.1777 },
  },
};

// Style dla różnych wariantów
const variantStyles: Record<OverlayVariant, {
  fill: string;
  fillOpacity: number;
  stroke: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  hasBlur: boolean;
}> = {
  normal: {
    fill: '#0064FF',
    fillOpacity: 0.15,
    stroke: 'white',
    strokeDasharray: '2 2',
    hasBlur: true,
  },
  replaced: {
    fill: '#0064FF',
    fillOpacity: 0.4,
    stroke: '#0064FF',
    strokeWidth: 2,
    hasBlur: true,
  },
  absence: {
    fill: '#A4B2C0',
    fillOpacity: 0.35,
    stroke: '#A4B2C0',
    strokeDasharray: '2 2',
    hasBlur: true,
  },
};

export function WindowOverlay({
  type,
  variant = 'normal',
  className = '',
  style,
  isHighlighted = false,
  onMouseEnter,
  onMouseLeave,
}: WindowOverlayProps) {
  const config = windowPaths[type];
  const variantStyle = variantStyles[variant];

  if (!config) return null;

  const uniqueId = `${config.clipPathId}_${variant}_${Math.random().toString(36).substring(2, 11)}`;

  const currentFill = isHighlighted ? '#0064FF' : variantStyle.fill;
  const currentFillOpacity = isHighlighted ? 0.4 : variantStyle.fillOpacity;
  const currentStroke = isHighlighted ? '#0064FF' : variantStyle.stroke;
  const currentStrokeWidth = isHighlighted ? 2 : (variantStyle.strokeWidth ?? 1);

  return (
    <svg
      width={config.width}
      height={config.height}
      viewBox={config.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ ...style, cursor: 'pointer' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Blur effect - tylko gdy hasBlur */}
      {variantStyle.hasBlur && config.foreignObjectSize && (
        <>
          <foreignObject
            x={config.foreignObjectSize.x}
            y={config.foreignObjectSize.y}
            width={config.foreignObjectSize.width}
            height={config.foreignObjectSize.height}
          >
            <div
              style={{
                backdropFilter: 'blur(1.5px)',
                clipPath: `url(#${uniqueId})`,
                height: '100%',
                width: '100%',
              }}
            />
          </foreignObject>
          <defs>
            <clipPath id={uniqueId} transform="translate(3 3)">
              <path d={config.path} />
            </clipPath>
          </defs>
        </>
      )}

      {/* Main path */}
      <path
        d={config.path}
        fill={currentFill}
        fillOpacity={currentFillOpacity}
        stroke={currentStroke}
        strokeWidth={currentStrokeWidth}
        strokeDasharray={isHighlighted ? undefined : variantStyle.strokeDasharray}
        strokeLinecap={variant === 'replaced' ? 'round' : undefined}
        style={{ transition: 'fill 0.2s, stroke 0.2s, fill-opacity 0.2s, stroke-width 0.2s' }}
      />
    </svg>
  );
}

export default WindowOverlay;
